import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../context/AdminContext';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Package, FileText, MessageSquare, ShoppingCart, IndianRupee, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { getAuthHeaders } = useAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/dashboard`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'bg-blue-500',
      bgLight: 'bg-blue-50',
    },
    {
      title: 'Total Orders',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'bg-green-500',
      bgLight: 'bg-green-50',
    },
    {
      title: 'Total Revenue',
      value: `₹${(stats?.total_revenue || 0).toLocaleString()}`,
      icon: IndianRupee,
      color: 'bg-cyan-500',
      bgLight: 'bg-cyan-50',
    },
    {
      title: 'Pending Orders',
      value: stats?.pending_orders || 0,
      icon: Clock,
      color: 'bg-orange-500',
      bgLight: 'bg-orange-50',
    },
    {
      title: 'Blog Posts',
      value: stats?.total_blog_posts || 0,
      icon: FileText,
      color: 'bg-purple-500',
      bgLight: 'bg-purple-50',
    },
    {
      title: 'Testimonials',
      value: stats?.total_testimonials || 0,
      icon: MessageSquare,
      color: 'bg-pink-500',
      bgLight: 'bg-pink-50',
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500">Overview of your store</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-20 bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {statCards.map((stat, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                    <div className={`p-4 rounded-full ${stat.bgLight}`}>
                      <stat.icon className={`h-8 w-8 text-white ${stat.color} rounded-full p-1.5`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <a href="/admin/products" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-center transition-colors">
                <Package className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                <span className="text-sm font-medium">Add Product</span>
              </a>
              <a href="/admin/blog" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-center transition-colors">
                <FileText className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                <span className="text-sm font-medium">New Blog Post</span>
              </a>
              <a href="/admin/testimonials" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-center transition-colors">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-pink-500" />
                <span className="text-sm font-medium">Add Testimonial</span>
              </a>
              <a href="/admin/orders" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 text-center transition-colors">
                <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <span className="text-sm font-medium">View Orders</span>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
